import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import bgimage from "../../img/no_image.png";
import { formatDaysAgo } from "../../_helpers/reusablefunctions/dateSetter";

const stripHtml = (html) =>
  typeof html === "string" ? html.replace(/<[^>]+>/g, "") : "";

const InternshipCard = ({ internship, onView, onEdit }) => {
  const {
    Title,
    BrandName,
    BrandLogoURL,
    JobDescription,
    SubTitle,
    JobLocation,
    TypesWorkplace,
    Course_Duration,
    Stipend,
    StipendAmount,
    created_at,
  } = internship || {};

  const logo = BrandLogoURL || bgimage;

  const description =
    SubTitle || stripHtml(JobDescription)?.trim() || "";

  const workModeLabel =
    TypesWorkplace === "remote"
      ? "Remote"
      : TypesWorkplace === "hybrid"
        ? "Hybrid"
        : TypesWorkplace
          ? "On-site"
          : "";

  const durationLabel = Course_Duration
    ? `${Course_Duration} Internship`
    : "Internship";

  const salaryLabel =
    Stipend === "free"
      ? "Unpaid"
      : StipendAmount
        ? `₹${Number(StipendAmount).toLocaleString("en-IN")} / month`
        : "--";

  const postedLabel = created_at
    ? `Posted ${formatDaysAgo(created_at)}`
    : "";

  return (
    <Card
      sx={{
        borderRadius: "16px",
        boxShadow: "0px 4px 10px rgba(0,0,0,0.06)",
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent sx={{ p: 2.5, pb: 2, flexGrow: 1 }}>
        {/* Top: Logo + Title + Brand */}
        <Box display="flex" alignItems="center" gap={2} mb={1.5}>
          <Box
            component="img"
            src={logo}
            alt={BrandName || Title || "brand"}
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              objectFit: "cover",
              backgroundColor: "#F5F5F5",
            }}
          />
          <Box flex={1}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                color: "#292929",
                lineHeight: 1.2,
              }}
            >
              {Title || "--"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#7A7A7A", mt: 0.5, fontSize: "14px" }}
            >
              {BrandName || "--"}
            </Typography>
          </Box>
        </Box>

        {/* Short Description */}
        <Typography
          variant="body2"
          sx={{
            color: description ? "#A6A6A6" : "transparent",
            mb: 1.5,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            minHeight: "40px",   // ensures fixed height for 2 lines
          }}
        >
          {description || "No description available."}
        </Typography>


        <Divider sx={{ my: 1.5 }} />

        {/* Location / Mode / Duration */}
        <Stack
          direction="row"
          spacing={2}
          flexWrap="wrap"
          alignItems="center"
          mb={1.5}
        >
          {JobLocation && (
            <Box display="flex" alignItems="center" gap={0.7}>
              <LocationOnIcon sx={{ fontSize: 18, color: "#B8B8B8" }} />
              <Typography variant="body2" sx={{ fontSize: "13px", color: "#7A7A7A" }}>
                {JobLocation}
              </Typography>
            </Box>
          )}

          {workModeLabel && (
            <Box display="flex" alignItems="center" gap={0.7}>
              <WorkOutlineIcon sx={{ fontSize: 18, color: "#B8B8B8" }} />
              <Typography variant="body2" sx={{ fontSize: "13px", color: "#7A7A7A" }}>
                {workModeLabel}
              </Typography>
            </Box>
          )}

          {durationLabel && (
            <Box display="flex" alignItems="center" gap={0.7}>
              <AccessTimeIcon sx={{ fontSize: 18, color: "#B8B8B8" }} />
              <Typography variant="body2" sx={{ fontSize: "13px", color: "#7A7A7A" }}>
                {durationLabel}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Salary + Posted */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: "14px", color: "#4F4F4F" }}
          >
            Salary:&nbsp;
            <span style={{ color: "#219653", fontWeight: 600 }}>
              {salaryLabel}
            </span>
          </Typography>

          {postedLabel && (
            <Box display="flex" alignItems="center" gap={0.6}>
              <AccessTimeIcon sx={{ fontSize: 18, color: "#B8B8B8" }} />
              <Typography
                variant="body2"
                sx={{ fontSize: "13px", color: "#7A7A7A" }}
              >
                {postedLabel}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>

      {/* Buttons */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="stretch"
        sx={{ px: 2, pb: 2, gap: 2 }}
      >
        {onEdit && (
          <Button
            fullWidth
            variant="outlined"
            onClick={onEdit}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#F14950",
              color: "#F14950",
              boxShadow: "none",
              "&:hover": {
                borderColor: "#b02a2a",
                boxShadow: "none",
              },
            }}
          >
            Edit
          </Button>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={onView}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: "#F14950",
            color: "#fff",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#b02a2a",
              boxShadow: "none",
            },
          }}
        >
          View
        </Button>
      </Stack>
    </Card>
  );
};

export default InternshipCard;
